import React, { useRef, useState, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Input,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
  Divider,
  Chip,
  LinearProgress,
  AspectRatio,
  Sheet,
  Select,
  Option,
  Modal,
  ModalDialog,
  ModalClose
} from '@mui/joy';
import Collapse from '@mui/material/Collapse';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatTitle } from '../../helpers/policyUtils';
import JsonValueEditor from './JsonValueEditor';

const IMAGE_TYPES = ['jpeg', 'jpg', 'png', 'gif', 'webp'];

/**
 * Get file extension from filename or URL
 */
const getFileType = (filename) => {
  if (!filename) return null;
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext;
};

/**
 * Check if file is an image
 */
const isImageFile = (filename) => {
  const ext = getFileType(filename);
  return IMAGE_TYPES.includes(ext);
};

/**
 * Accept both boolean and legacy string values from persisted policies
 */
const normalizeBooleanValue = (value) => {
  if (value === true || value === false) return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

/**
 * A field is considered set when it is neither null/undefined nor an empty string.
 * This intentionally treats boolean false as a valid value.
 */
const isFieldValueSet = (value) => value !== undefined && value !== null && value !== '';

/**
 * Document Thumbnail Component
 */
const DocumentThumbnail = ({ doc, isSelected, onClick, onDelete, index, fieldsFilled, totalFields }) => {
  const isImage = isImageFile(doc.name);
  const completionPercent = totalFields > 0 ? Math.round((fieldsFilled / totalFields) * 100) : 0;
  
  return (
    <Card
      variant={isSelected ? 'solid' : 'outlined'}
      color={isSelected ? 'primary' : 'neutral'}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative',
        '&:hover': {
          boxShadow: 'md',
          transform: 'translateY(-2px)',
        },
        ...(isSelected && {
          boxShadow: 'lg',
        }),
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 1.5 }}>
        <Stack spacing={1}>
          {/* Thumbnail Preview */}
          <AspectRatio ratio="4/3" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
            {isImage && doc.url ? (
              <img
                src={doc.url}
                alt={doc.name}
                style={{ objectFit: 'cover' }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isSelected ? 'primary.100' : 'neutral.100',
                }}
              >
                <DescriptionIcon sx={{ fontSize: 40, color: isSelected ? 'primary.500' : 'neutral.500' }} />
              </Box>
            )}
          </AspectRatio>

          {/* Document Info */}
          <Stack spacing={0.5}>
            <Typography
              level="body-xs"
              fontWeight="md"
              noWrap
              sx={{ color: isSelected ? 'primary.solidColor' : 'inherit' }}
            >
              {index + 1}. {doc.name}
            </Typography>

            {/* Completion Progress */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <LinearProgress
                determinate
                value={completionPercent}
                sx={{
                  flexGrow: 1,
                  '--LinearProgress-thickness': '4px',
                  ...(completionPercent === 100 && {
                    '--LinearProgress-progressColor': 'var(--joy-palette-success-500)',
                  }),
                }}
              />
              <Typography level="body-xs" sx={{ minWidth: 32, color: isSelected ? 'primary.solidColor' : 'neutral.500' }}>
                {completionPercent}%
              </Typography>
            </Stack>
          </Stack>

          {/* Delete Button */}
          <IconButton
            size="sm"
            variant="soft"
            color="danger"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              opacity: 0.8,
              '&:hover': { opacity: 1 },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(doc.id);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

/**
 * Upload Drop Zone Component
 */
const UploadDropZone = ({ onUpload, isUploading, isDragActive, onDragEnter, onDragLeave, onDrop }) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      await onUpload(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderStyle: 'dashed',
        borderWidth: 2,
        borderColor: isDragActive ? 'primary.500' : 'neutral.300',
        bgcolor: isDragActive ? 'primary.50' : 'transparent',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.400',
          bgcolor: 'primary.50',
        },
      }}
      onClick={() => !isUploading && fileInputRef.current?.click()}
      onDragEnter={onDragEnter}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <CardContent sx={{ py: 3 }}>
        <Stack alignItems="center" spacing={1}>
          {isUploading ? (
            <>
              <CircularProgress size="md" />
              <Typography level="body-sm" color="neutral">
                Uploading...
              </Typography>
            </>
          ) : (
            <>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'primary.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 24, color: 'primary.500' }} />
              </Box>
              <Typography level="body-sm" fontWeight="md">
                Drop files here or click to upload
              </Typography>
              <Typography level="body-xs" color="neutral">
                Supports PDF, JPG, PNG
              </Typography>
            </>
          )}
        </Stack>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="application/pdf,image/*"
          multiple
          hidden
        />
      </CardContent>
    </Card>
  );
};

/**
 * Field Input Component - Renders appropriate input based on field type
 */
const FieldInput = ({ field, value, onChange }) => {
  const getInputByType = () => {
    switch (field.type) {
      case 'boolean':
        return (
          <Select
            size="sm"
            value={normalizeBooleanValue(value)}
            onChange={(_, newVal) => onChange(newVal)}
            placeholder="Select value..."
          >
            <Option value={true}>True</Option>
            <Option value={false}>False</Option>
          </Select>
        );
      case 'number':
        return (
          <Input
            size="sm"
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter expected ${formatTitle(field.key).toLowerCase()}...`}
          />
        );
      case 'date':
        return (
          <Input
            size="sm"
            type="text"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.format || 'YYYY-MM-DD'}
          />
        );
      case 'json':
        return (
          <JsonValueEditor
            value={value}
            onChange={onChange}
          />
        );
      default:
        return (
          <Input
            size="sm"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter expected ${formatTitle(field.key).toLowerCase()}...`}
          />
        );
    }
  };

  return getInputByType();
};

/**
 * Document Detail Panel - Shows preview and fields for selected document
 */
const DocumentDetailPanel = ({ 
  doc, 
  documentFieldsSchema, 
  onFieldChange
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const isImage = isImageFile(doc?.name);
  const isPdf = getFileType(doc?.name) === 'pdf';

  if (!doc) {
    return (
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minHeight: 400 }}>
        <Card variant="outlined" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CardContent>
            <Stack alignItems="center" spacing={2}>
              <DescriptionIcon sx={{ fontSize: 64, color: 'neutral.300' }} />
              <Typography level="body-md" color="neutral">
                Select a document to view details
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  const filledFields = documentFieldsSchema.filter(
    f => isFieldValueSet(doc.documentFields?.[f.key])
  ).length;
  const completionPercent = documentFieldsSchema.length > 0 
    ? Math.round((filledFields / documentFieldsSchema.length) * 100) 
    : 0;

  return (
    <>
      {/* Fullscreen Preview Modal */}
      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <ModalDialog
          layout="fullscreen"
          sx={{ p: 0, overflow: 'hidden' }}
        >
          <ModalClose />
          <Box sx={{ height: '100%', width: '100%', p: 2 }}>
            {isPdf ? (
              <embed
                src={doc.url}
                style={{ width: '100%', height: '100%', border: 'none' }}
                type="application/pdf"
              />
            ) : isImage ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={doc.url}
                  alt={doc.name}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              </Box>
            ) : null}
          </Box>
        </ModalDialog>
      </Modal>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minHeight: 400 }}>
        {/* Left Side: Document Preview */}
        <Card variant="outlined" sx={{ flex: { xs: 'none', md: '0 0 50%' }, minWidth: 0 }}>
          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography level="title-md" startDecorator={<DescriptionIcon />}>
                {doc.name}
              </Typography>
              {doc.url && (
                <Tooltip title="View fullscreen">
                  <IconButton
                    size="sm"
                    variant="outlined"
                    onClick={() => setPreviewOpen(true)}
                  >
                    <FullscreenIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            {/* Preview - takes remaining space */}
            {doc.url && (
              <Box sx={{ flexGrow: 1, minHeight: 300, borderRadius: 'md', overflow: 'hidden', mb: 1 }}>
                {isPdf ? (
                  <embed
                    src={doc.url}
                    style={{ width: '100%', height: '100%', border: 'none', minHeight: 300 }}
                    type="application/pdf"
                  />
                ) : isImage ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'neutral.50',
                    }}
                  >
                    <img
                      src={doc.url}
                      alt={doc.name}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'neutral.100',
                    }}
                  >
                    <DescriptionIcon sx={{ fontSize: 64, color: 'neutral.400' }} />
                  </Box>
                )}
              </Box>
            )}

            {/* Completion Status */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 'auto' }}>
              <LinearProgress
                determinate
                value={completionPercent}
                sx={{
                  flexGrow: 1,
                  '--LinearProgress-thickness': '8px',
                  borderRadius: 'sm',
                  ...(completionPercent === 100 && {
                    '--LinearProgress-progressColor': 'var(--joy-palette-success-500)',
                  }),
                }}
              />
              <Chip
                size="sm"
                variant="soft"
                color={completionPercent === 100 ? 'success' : 'neutral'}
                startDecorator={completionPercent === 100 ? <CheckCircleIcon /> : null}
              >
                {filledFields}/{documentFieldsSchema.length} fields
              </Chip>
            </Stack>
          </CardContent>
        </Card>

        {/* Right Side: Fields Section */}
        <Card variant="outlined" sx={{ flex: { xs: 'none', md: '1 1 50%' }, minWidth: 0 }}>
          <CardContent sx={{ height: '100%', maxHeight: { xs: '50vh', md: '60vh' }, overflow: 'auto' }}>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Expected Field Values
            </Typography>
            
            <Stack spacing={2}>
              {documentFieldsSchema.map((field) => {
                const hasValue = isFieldValueSet(doc.documentFields?.[field.key]);
                
                return (
                  <Sheet
                    key={field.key}
                    variant={hasValue ? 'soft' : 'outlined'}
                    color={hasValue ? 'success' : 'neutral'}
                    sx={{
                      p: 2,
                      borderRadius: 'md',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Stack spacing={1}>
                      {/* Field Header */}
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        {hasValue ? (
                          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.500' }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'neutral.400' }} />
                        )}
                        <Typography level="body-sm" fontWeight="lg">
                          {formatTitle(field.key)}
                        </Typography>
                        <Chip size="sm" variant="soft" color="neutral">
                          {field.type}
                        </Chip>
                        {field.description && (
                          <Tooltip title={field.description} arrow placement="top" sx={{ maxWidth: 350 }}>
                            <IconButton size="sm" variant="plain" color="neutral">
                              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>

                      {/* Field Input */}
                      <FieldInput
                        field={field}
                        value={doc.documentFields?.[field.key]}
                        onChange={(value) => onFieldChange(doc.id, field.key, value)}
                      />
                    </Stack>
                  </Sheet>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </>
  );
};

/**
 * Evaluation Documents Step Component
 * Handles document upload and field entry for evaluation policies
 */
const EvaluationDocumentsStep = ({
  currentPolicy,
  setCurrentPolicy,
  linkedCompliancePolicy,
  addEvaluationDocument,
  isUploadingDocument
}) => {
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const documentsContainerRef = useRef(null);

  const documents = useMemo(() => currentPolicy?.policy?.documents || [], [currentPolicy?.policy?.documents]);
  const documentFieldsSchema = linkedCompliancePolicy?.policy?.documentFieldsSchema || [];
  const selectedDoc = documents.find(d => d.id === selectedDocId) || null;
  const isDocumentSelected = Boolean(selectedDoc);
  const isSelectorDrawerOpen = !isDocumentSelected;

  // Clear stale selection when documents list changes
  React.useEffect(() => {
    if (selectedDocId && !documents.some(doc => doc.id === selectedDocId)) {
      setSelectedDocId(null);
    }
  }, [documents, selectedDocId]);

  const handleRemoveDocument = useCallback((docId) => {
    // Find the document to be removed
    const docToRemove = documents.find(doc => doc.id === docId);
    
    // Revoke blob URL if this is a pending document (not yet uploaded)
    if (docToRemove?.url && docToRemove.url.startsWith('blob:')) {
      URL.revokeObjectURL(docToRemove.url);
    }
    
    const updatedDocuments = documents.filter(doc => doc.id !== docId);
    setCurrentPolicy({
      ...currentPolicy,
      policy: {
        ...currentPolicy.policy,
        documents: updatedDocuments
      }
    });
    
    if (selectedDocId === docId || updatedDocuments.length === 0) {
      setSelectedDocId(null);
    }
  }, [documents, currentPolicy, setCurrentPolicy, selectedDocId]);

  const handleFieldChange = useCallback((docId, fieldKey, value) => {
    const updatedDocuments = documents.map(doc => {
      if (doc.id !== docId) return doc;
      return {
        ...doc,
        documentFields: {
          ...doc.documentFields,
          [fieldKey]: value
        }
      };
    });
    setCurrentPolicy({
      ...currentPolicy,
      policy: {
        ...currentPolicy.policy,
        documents: updatedDocuments
      }
    });
  }, [documents, currentPolicy, setCurrentPolicy]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = Array.from(e.dataTransfer?.files || []);
    for (const file of files) {
      if (file.type.startsWith('image/') || file.type === 'application/pdf') {
        addEvaluationDocument(file);
      }
    }
  }, [addEvaluationDocument]);

  const scrollDocuments = (direction) => {
    if (documentsContainerRef.current) {
      const scrollAmount = 200;
      documentsContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleSelectorHeaderClick = useCallback(() => {
    if (!isSelectorDrawerOpen) {
      setSelectedDocId(null);
    }
  }, [isSelectorDrawerOpen]);

  // Check if linked policy ID is set
  const linkedPolicyId = currentPolicy?.settings?.linked_policy_id;

  // No linked policy ID configured
  if (!linkedPolicyId) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 16, p: 6, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'warning.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 40, color: 'warning.500' }} />
          </Box>
          <Typography level="title-lg" color="warning">
            No Compliance Policy Linked
          </Typography>
          <Typography level="body-md" color="neutral" sx={{ maxWidth: 400 }}>
            This evaluation policy does not have a linked compliance policy.
            Please go to Settings and select a compliance policy to link.
          </Typography>
        </Stack>
      </Card>
    );
  }

  // Loading state for linked policy
  if (!linkedCompliancePolicy) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 16, p: 6, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size="lg" />
          <Typography level="body-lg">
            Loading linked compliance policy...
          </Typography>
        </Stack>
      </Card>
    );
  }

  // No fields defined
  if (documentFieldsSchema.length === 0) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 16, p: 6, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'warning.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 40, color: 'warning.500' }} />
          </Box>
          <Typography level="title-lg" color="warning">
            No Fields Defined
          </Typography>
          <Typography level="body-md" color="neutral" sx={{ maxWidth: 400 }}>
            The linked compliance policy "{linkedCompliancePolicy?.name}" has no document fields defined.
            Please add fields to the compliance policy first.
          </Typography>
        </Stack>
      </Card>
    );
  }

  // Calculate overall progress
  const totalExpectedFields = documents.length * documentFieldsSchema.length;
  const totalFilledFields = documents.reduce((acc, doc) => {
    return acc + documentFieldsSchema.filter(f => isFieldValueSet(doc.documentFields?.[f.key])).length;
  }, 0);
  const overallProgress = totalExpectedFields > 0 
    ? Math.round((totalFilledFields / totalExpectedFields) * 100) 
    : 0;

  return (
    <Stack spacing={2}>
      {/* Header with Stats */}
      <Card variant="soft" sx={{ borderRadius: 12 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Chip variant="solid" color="primary" size="lg">
                {documents.length}
              </Chip>
              <Typography level="body-md">
                Document{documents.length !== 1 ? 's' : ''} uploaded
              </Typography>
            </Stack>
            
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography level="body-sm" color="neutral">
                Overall completion:
              </Typography>
              <LinearProgress
                determinate
                value={overallProgress}
                sx={{
                  width: 120,
                  '--LinearProgress-thickness': '8px',
                  borderRadius: 'sm',
                  ...(overallProgress === 100 && {
                    '--LinearProgress-progressColor': 'var(--joy-palette-success-500)',
                  }),
                }}
              />
              <Typography level="body-sm" fontWeight="lg">
                {overallProgress}%
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Document Selector Drawer */}
      <Sheet variant="outlined" sx={{ borderRadius: 12, p: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography level="title-sm">
              Document Selector
            </Typography>
            <Typography level="body-sm" color="neutral" noWrap>
              {isDocumentSelected ? `Selected: ${selectedDoc.name}` : 'No document selected'}
            </Typography>
          </Stack>

          <IconButton
            size="sm"
            variant="soft"
            color="neutral"
            aria-label={isSelectorDrawerOpen ? 'Document selector is open' : 'Expand document selector'}
            onClick={handleSelectorHeaderClick}
            disabled={isSelectorDrawerOpen}
          >
            <ExpandMoreIcon
              sx={{
                transform: isSelectorDrawerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.25s ease',
              }}
            />
          </IconButton>
        </Stack>

        <Collapse in={isSelectorDrawerOpen} timeout={250} unmountOnExit>
          <Box sx={{ position: 'relative', mt: 1.5 }}>
            {documents.length > 3 && (
              <>
                <IconButton
                  size="sm"
                  variant="soft"
                  sx={{
                    position: 'absolute',
                    left: -12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                  }}
                  onClick={() => scrollDocuments('left')}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  size="sm"
                  variant="soft"
                  sx={{
                    position: 'absolute',
                    right: -12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                  }}
                  onClick={() => scrollDocuments('right')}
                >
                  <ChevronRightIcon />
                </IconButton>
              </>
            )}

            <Box
              ref={documentsContainerRef}
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                pb: 1,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {/* Upload Card */}
              <Box sx={{ minWidth: 180, flexShrink: 0 }}>
                <UploadDropZone
                  onUpload={addEvaluationDocument}
                  isUploading={isUploadingDocument}
                  isDragActive={isDragActive}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                />
              </Box>

              {/* Document Thumbnails */}
              {documents.map((doc, index) => {
                const fieldsFilled = documentFieldsSchema.filter(f => isFieldValueSet(doc.documentFields?.[f.key])).length;
                return (
                  <Box key={doc.id} sx={{ minWidth: 180, flexShrink: 0 }}>
                    <DocumentThumbnail
                      doc={doc}
                      index={index}
                      isSelected={selectedDoc?.id === doc.id}
                      onClick={() => setSelectedDocId(doc.id)}
                      onDelete={handleRemoveDocument}
                      fieldsFilled={fieldsFilled}
                      totalFields={documentFieldsSchema.length}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Collapse>
      </Sheet>

      <Divider />

      {/* Document Detail Panel */}
      <Box>
        {documents.length === 0 ? (
          <Card 
            variant="outlined" 
            sx={{ 
              minHeight: 300,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              borderStyle: 'dashed',
              borderColor: 'neutral.300',
            }}
          >
            <CardContent>
              <Stack alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'neutral.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 40, color: 'neutral.400' }} />
                </Box>
                <Typography level="title-lg" color="neutral">
                  No Documents Yet
                </Typography>
                <Typography level="body-md" color="neutral" sx={{ maxWidth: 400, textAlign: 'center' }}>
                  Upload documents to start entering expected field values.
                  You can drag and drop files or click the upload area above.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <DocumentDetailPanel
            doc={selectedDoc}
            documentFieldsSchema={documentFieldsSchema}
            onFieldChange={handleFieldChange}
          />
        )}
      </Box>
    </Stack>
  );
};

export default EvaluationDocumentsStep;
